import * as React from 'react';
import { parseCookies } from '@cian/utils/lib/shared/cookie';
import { useSelector } from 'react-redux';

import { selectBrokerConsultantBannerAvailable } from '../../selectors/brokerConsultantBanner';
import { getRandomBoolean } from '../../selectors/getRandomBoolean';
import { useApplicationContext } from '../../utils/applicationContext';
import { selectIsMortgageRateHikeBannerAvailable } from '../../selectors/mortgageRateHikeBanner';

type TRotatedBanners = 'broker' | 'mortgageRateHike';

const BANNER_TEST_COOKIE_NAME = 'newbuilding_search_banner_test';

function getBannerTestCookie(): TRotatedBanners | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const cookie = parseCookies(window.document.cookie)[BANNER_TEST_COOKIE_NAME];

  return (cookie as TRotatedBanners) || null;
}

export function useRotatingBanner(): TRotatedBanners | null {
  const { config } = useApplicationContext();
  const isBrokerConsultantBannerAvailable = useSelector(selectBrokerConsultantBannerAvailable);
  const isMortgageRateHikeBannerAvailableByStatus = useSelector(selectIsMortgageRateHikeBannerAvailable);
  const randomBoolean = useSelector(getRandomBoolean);
  const isMortgageRateHikeBannerEnabled = config.get<boolean>('Newbuilding.MortgageRateHikeBanner.Enabled') || false;
  const bannerTestCookie = getBannerTestCookie();
  const isMortgageRateHikeBannerAvailable =
    isMortgageRateHikeBannerEnabled && isMortgageRateHikeBannerAvailableByStatus;

  return React.useMemo(() => {
    if (bannerTestCookie === 'broker') {
      return 'broker';
    }

    if (bannerTestCookie === 'mortgageRateHike' && isMortgageRateHikeBannerAvailable) {
      return 'mortgageRateHike';
    }

    const banners: Array<TRotatedBanners> = [];

    if (isMortgageRateHikeBannerAvailable) {
      banners.push('mortgageRateHike');
    }

    if (isBrokerConsultantBannerAvailable) {
      banners.push('broker');
    }

    if (banners.length === 0) {
      return null;
    }

    if (banners.length === 1) {
      return banners[0];
    }

    return randomBoolean ? banners[0] : banners[1];
  }, [bannerTestCookie, randomBoolean, isMortgageRateHikeBannerAvailable, isBrokerConsultantBannerAvailable]);
}
